/**
 * Общая «кухня» списка отработанных игр: и вкладка «Загрузка» в Персонале (свод по всем
 * сотрудникам филиала — смотрят кассир и управляющий), и личный экран «Мои игры» (свой
 * список у самого сотрудника) считаются ОДНИМ кодом, чтобы в конце месяца ведущий и
 * кассир сверялись по одинаковым цифрам, а не по двум похожим реализациям.
 *
 * Денег здесь нет и быть не должно: расчёт зарплаты с заказчиком не согласован.
 */

import { DEAL_TO_FUNNEL, type Deal, type Employee, type GameFormat, type ID, type Team } from '@/types';

/** Роли на событии (ключ → русское имя) — те же подписи, что в карточке сделки (DealEditor). */
export const ROLE_RU: Record<string, string> = {
  vedushiy: 'Ведущий 1', vedushiy2: 'Ведущий 2', sovedushiy: 'Соведущий', didjey: 'Диджей', hostes: 'Хостес', voditel: 'Водитель', stajer: 'Стажёр',
  host: 'Ведущий', tech: 'Диджей', admin: 'Ведущий', manager: 'Хостес', event_manager: 'Соведущий',
  /** Агентские 5% за личную продажу — отдельная строка в зарплате, не роль на игре. */
  agent: 'Агентские (5%)',
};

/**
 * Дольше этого игра быть не может — всё, что больше, это опечатка при вводе времени.
 * В боевой базе такие есть: у одной игры перепутан месяц в дате окончания.
 */
const MAX_SANE_MINUTES = 12 * 60;

/**
 * Сколько игра реально длилась, в минутах, и надёжна ли эта цифра.
 *
 * Считаем ТОЛЬКО по фактическому времени игры (с 18:00 до 21:00 — это 3 часа). Выпадающий
 * список «Длительность игры» из Битрикса здесь НЕ используется намеренно (решение заказчика
 * 30.08.2026): в нём всего четыре значения — 60, 90, 120 и «120+», и последнее превращалось
 * ровно в 2 часа, из-за чего часы занижались вдвое (на боевом 102 игры с «120+» идут в
 * среднем по 4.5 часа). Смешивать два источника хуже, чем не считать вовсе: в одном отчёте
 * получались бы цифры, посчитанные по разным правилам, и объяснить разницу невозможно.
 *
 * suspicious=true — время не заполнено или заполнено с ошибкой (конец раньше начала, длина
 * больше полусуток). Тогда минут ноль: в отчёте будет прочерк и пометка «проверить», а не
 * выдуманное число. Таких игр в базе пять, все с опечаткой в дате окончания.
 */
export function gameDuration(d: Deal): { minutes: number; suspicious: boolean } {
  const { start_at: s, end_at: e } = d.event;
  if (s == null || e == null) return { minutes: 0, suspicious: true };
  const fact = (e - s) / 60_000;
  if (fact <= 0 || fact > MAX_SANE_MINUTES) return { minutes: 0, suspicious: true };
  return { minutes: fact, suspicious: false };
}

/** Игровое время в минутах (без пометки о надёжности) — см. gameDuration. */
export function gameMinutes(d: Deal): number {
  return gameDuration(d).minutes;
}

/** Русская подпись роли сотрудника на конкретной игре («В команде» — если поимённого назначения не было). */
export function roleLabel(role: string | undefined): string {
  return role ? (ROLE_RU[role] ?? role) : 'В команде';
}

/** дд.мм.гг — как в списках сделок на дашборде. */
export function ddmmyy(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getFullYear()).slice(2)}`;
}

export function hhmm(ts?: number | null): string {
  if (ts == null) return '';
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Интервал «19:00 – 21:00» (правая часть опускается, если конца нет). */
export function timeRange(d: Deal, shift: number): string {
  const s = d.event.start_at != null ? hhmm(d.event.start_at + shift) : '';
  const e = d.event.end_at != null ? hhmm(d.event.end_at + shift) : '';
  if (!s) return '—';
  return e ? `${s} – ${e}` : s;
}

/** Название игры для списка: своё название события, иначе имя клиента. */
export function gameTitle(d: Deal): string {
  return (d.event.title || '').trim() || (d.contact_name || '').trim() || `#${d.number}`;
}

export const MONTHS_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

/** «2026-08» → «Август 2026» — для шапки списка игр. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return `${MONTHS_RU[m - 1] ?? m} ${y}`;
}

/** Текущий месяц в формате `<input type="month">` — общее начальное значение обоих экранов. */
export function currentMonthKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Название формата игры по справочнику (в выгрузке и в списке — одинаково). */
export function formatName(formats: GameFormat[], d: Deal): string {
  const id = d.event.game_format_id;
  if (!id) return '';
  return formats.find((f) => f.id === id)?.name ?? '';
}

/** Одна строка «сотрудник × игра» — накапливается вместе с агрегатом «Загрузки». */
export type WorkloadGame = { deal: Deal; role: string | undefined };

/** Итог по одному сотруднику за месяц + сам список его игр. */
export type WorkloadRow = {
  id: ID;
  name: string;
  /** Наиболее частая роль за месяц (для колонки «Роль»). */
  role: string;
  games: number;
  hours: number;
  items: WorkloadGame[];
};

/**
 * Проведённые игры/часы линейного персонала за месяц.
 *
 * @param onlyEmployeeId если задан — считаем ТОЛЬКО этого сотрудника (экран «Мои игры»).
 *        Так личный список гарантированно строится тем же кодом, что и общий свод.
 */
export function computeWorkloadRows(opts: {
  deals: Deal[];
  employees: Employee[];
  teams: Team[];
  /** «2026-08» */
  month: string;
  /** Глобальный фильтр филиала ('all' — не ограничивать). */
  branchId: ID | 'all';
  onlyEmployeeId?: ID | null;
}): WorkloadRow[] {
  const { deals, employees, teams, month, branchId, onlyEmployeeId } = opts;
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return [];
  const monthStart = new Date(y, m - 1, 1).getTime();
  const monthEnd = new Date(y, m, 1).getTime();
  const empById = new Map(employees.map((e) => [e.id, e]));
  const teamById = new Map(teams.map((t) => [t.id, t]));

  type Acc = { games: number; minutes: number; roleCounts: Map<string, number>; items: WorkloadGame[] };
  const acc = new Map<string, Acc>();

  for (const d of deals) {
    // Проведённая игра: funnel_status === 'done' (с fallback по грубому статусу).
    const fs = d.funnel_status ?? DEAL_TO_FUNNEL[d.status];
    if (fs !== 'done') continue;
    // Отбор по месяцу — по дате события.
    const dt = d.event.start_at ?? d.event.event_date;
    if (dt == null || dt < monthStart || dt >= monthEnd) continue;
    // Глобальный фильтр филиала.
    if (branchId !== 'all' && d.branch_id !== branchId) continue;

    const minutes = gameMinutes(d);

    // Эффективный состав = объединение поимённых назначений и участников команды.
    const memberSet = new Set<string>();
    const roleByEmp = new Map<string, string>();
    for (const s of d.staff_assignments ?? []) {
      if (!s.employee_id) continue;
      memberSet.add(s.employee_id);
      if (s.role) roleByEmp.set(s.employee_id, s.role);
    }
    if (d.team_id) {
      const team = teamById.get(d.team_id);
      if (team) for (const mid of team.member_ids) memberSet.add(mid);
    }

    for (const empId of memberSet) {
      if (onlyEmployeeId && empId !== onlyEmployeeId) continue; // личный экран — только свои игры
      if (!empById.has(empId)) continue; // только линейный персонал из нашей БД
      let a = acc.get(empId);
      if (!a) { a = { games: 0, minutes: 0, roleCounts: new Map(), items: [] }; acc.set(empId, a); }
      a.games += 1;
      a.minutes += minutes;
      const role = roleByEmp.get(empId);
      if (role) a.roleCounts.set(role, (a.roleCounts.get(role) ?? 0) + 1);
      // Копим сам список игр: заказчику нужна не только цифра, но и «какие именно игры».
      a.items.push({ deal: d, role });
    }
  }

  return Array.from(acc.entries()).map(([empId, a]) => {
    const emp = empById.get(empId)!;
    // Наиболее частая роль по засчитанным играм; если роли не было нигде — «В команде».
    let topRole = ''; let topCount = 0;
    for (const [r, c] of a.roleCounts) if (c > topCount) { topCount = c; topRole = r; }
    const role = topRole ? (ROLE_RU[topRole] ?? topRole) : 'В команде';
    // Внутри карточки сотрудника игры идут по дате — так проще сверяться со своим списком.
    const items = a.items.slice().sort((x, y2) => {
      const tx = x.deal.event.start_at ?? x.deal.event.event_date ?? 0;
      const ty = y2.deal.event.start_at ?? y2.deal.event.event_date ?? 0;
      return tx - ty;
    });
    return { id: empId, name: emp.full_name, role, games: a.games, hours: a.minutes / 60, items };
  }).sort((a, b) => b.games - a.games || a.name.localeCompare(b.name));
}

/**
 * Плоский лист «сотрудник × игра» для Excel. Тот же набор колонок в обоих экранах —
 * ведущий и кассир должны приносить на сверку одинаковые таблицы.
 */
export const WORKLOAD_GAMES_HEAD = ['Сотрудник', 'Дата', 'Время', 'Номер игры', 'Название', 'Формат', 'Длительность (часы)', 'Роль на игре', 'Филиал', 'Проверить время'];

export function workloadGameRow(opts: {
  employeeName: string;
  item: WorkloadGame;
  formats: GameFormat[];
  branchName: string;
  tzShiftMs: number;
}): unknown[] {
  const { employeeName, item, formats, branchName, tzShiftMs } = opts;
  const d = item.deal;
  const ts = d.event.start_at ?? d.event.event_date;
  const dur = gameDuration(d);
  return [
    employeeName,
    ts != null ? ddmmyy(ts + tzShiftMs) : '',
    timeRange(d, tzShiftMs),
    d.number ?? '',
    gameTitle(d),
    formatName(formats, d),
    Number((dur.minutes / 60).toFixed(2)),
    roleLabel(item.role),
    branchName,
    // Часы посчитаны не по времени игры (оно не заполнено или заполнено с ошибкой) —
    // такую строку нужно проверить руками, прежде чем считать по ней выплату.
    dur.suspicious ? 'да' : '',
  ];
}
